import type { ReactNode } from "react";
import { disponibilidadLabel, tipoAfiliadoLabel } from "@/lib/affiliateEstados";
import { StepShell } from "./ui";
import type { AfiliacionForm } from "./types";

interface StepRevisionProps {
  form: AfiliacionForm;
  goTo: (step: number) => void;
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-background-200 py-2.5 last:border-0">
      <dt className="text-xs font-label uppercase tracking-wide text-foreground-500">{label}</dt>
      <dd className="max-w-[60%] text-right text-sm font-medium text-foreground-900">
        {value && String(value).trim() ? value : "—"}
      </dd>
    </div>
  );
}

function Block({
  title,
  icon,
  step,
  goTo,
  children,
}: {
  title: string;
  icon: string;
  step: number;
  goTo: (step: number) => void;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-heading font-semibold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className={`${icon} text-sm leading-none`} aria-hidden="true"></i>
          </span>
          {title}
        </h3>
        <button
          type="button"
          onClick={() => goTo(step)}
          className="cursor-pointer text-xs font-label font-semibold text-primary-700 transition-colors hover:text-primary-800"
        >
          Editar
        </button>
      </div>
      {children}
    </div>
  );
}

export default function StepRevision({ form, goTo }: StepRevisionProps) {
  const isEmpresa = form.tipo === "empresa";
  const nombreMostrar = isEmpresa
    ? form.empresaNombreComercial || form.empresaRazonSocial
    : form.nombreCompleto;

  return (
    <StepShell
      title="Revisa tu solicitud"
      description="Verifica que toda la información sea correcta antes de enviarla. Puedes volver a cualquier paso para corregir algo."
    >
      <div className="space-y-4">
        <Block title="Tipo de afiliación" icon="ri-user-settings-line" step={0} goTo={goTo}>
          <dl>
            <Row label="Tipo" value={tipoAfiliadoLabel(form.tipo)} />
          </dl>
        </Block>

        <Block
          title={isEmpresa ? "Información de la empresa" : "Datos personales"}
          icon={isEmpresa ? "ri-building-2-line" : "ri-user-line"}
          step={1}
          goTo={goTo}
        >
          <dl>
            {isEmpresa ? (
              <>
                <Row label="Nombre comercial" value={form.empresaNombreComercial} />
                <Row label="Razón social" value={form.empresaRazonSocial} />
                <Row label="RNC" value={form.rnc} />
                <Row label="Persona de contacto" value={form.contactoNombre} />
                <Row label="Cargo" value={form.contactoCargo} />
              </>
            ) : (
              <>
                <Row label="Nombre" value={form.nombreCompleto} />
                <Row label="Cédula" value={form.cedula} />
                <Row label="Participa como" value={form.rolVehiculo} />
              </>
            )}
            <Row label="Teléfono" value={form.telefono} />
            <Row label="WhatsApp" value={form.whatsapp} />
            <Row label="Correo" value={form.email} />
            <Row label="Provincia" value={form.provincia} />
            <Row label="Ciudad" value={form.ciudad} />
            <Row label="Dirección / zona" value={form.direccionZona} />
          </dl>
        </Block>

        <Block title={`Vehículos (${form.vehiculos.length})`} icon="ri-truck-line" step={2} goTo={goTo}>
          <ul className="space-y-2">
            {form.vehiculos.map((veh, index) => (
              <li
                key={veh.id}
                className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-sm font-label font-semibold text-secondary-900">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground-900">
                    {veh.tipo === "Otro" ? veh.tipoPersonalizado : veh.tipo} · {veh.marca} {veh.modelo}
                  </span>
                  <span className="block truncate text-xs text-foreground-500">
                    Placa {veh.placa || "—"} · {disponibilidadLabel(veh.disponibilidad)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Block>

        <Block title="Servicios y disponibilidad" icon="ri-service-line" step={3} goTo={goTo}>
          <dl>
            <Row label="Servicios" value={form.servicios.join(", ")} />
            <Row label="Zona" value={form.dispZona} />
            <Row label="Días" value={form.dispDias} />
            <Row label="Horario" value={form.dispHorario} />
            <Row label="Distancia máxima" value={form.dispDistancia} />
          </dl>
        </Block>

        <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 text-sm text-foreground-700">
          <i className="ri-checkbox-circle-line text-lg leading-none text-primary-600" aria-hidden="true"></i>
          {nombreMostrar ? (
            <span>
              La solicitud quedará registrada a nombre de <strong>{nombreMostrar}</strong>.
            </span>
          ) : (
            <span>Revisa que el nombre esté completo en el paso anterior.</span>
          )}
        </div>
      </div>
    </StepShell>
  );
}